import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";

import type { StoryMarkerGroup } from "./storyRows";
import { storyThumbnailUrl } from "./storyRows";
import "./listingStoryMarkers.css";

/** The static-map thumbnail is requested at 2x the card's size. */
const THUMB_W = 144;
const THUMB_H = 92;

interface Options {
  map: mapboxgl.Map | null;
  /** One marker per group, at its extent's centre. Empty = no markers. */
  groups: StoryMarkerGroup[];
  /** Groups whose marker is emphasised (their rows are hovered in the table). */
  hoveredKeys: ReadonlySet<string>;
  onHover?: (key: string | null) => void;
  onOpenStory?: (storyId: string) => void;
  /** A click on a stack of several stories: the page shows a list to pick from. */
  onOpenStack?: (group: StoryMarkerGroup, anchor: HTMLElement) => void;
}

/**
 * Cover-image thumbnails on the listing map, one per project (or folder)
 * that has story maps, stacked when there are several. HTML markers rather
 * than a symbol layer: the thumbnails are images fetched per story, and the
 * card/stack/badge look is plain CSS. Follows ProjectExpandButtons' pattern:
 * markers rebuilt when the groups change, removed on cleanup.
 */
export function useListingStoryMarkers({ map, groups, hoveredKeys, onHover, onOpenStory, onOpenStack }: Options): void {
  const markersRef = useRef<Map<string, { marker: mapboxgl.Marker; el: HTMLElement }>>(new Map());
  const onHoverRef = useRef(onHover);
  const onOpenStoryRef = useRef(onOpenStory);
  const onOpenStackRef = useRef(onOpenStack);
  onHoverRef.current = onHover;
  onOpenStoryRef.current = onOpenStory;
  onOpenStackRef.current = onOpenStack;

  // The groups' identity changes on every render of the page; rebuild only
  // when what they show changes.
  const signature = groups
    .map((g) => `${g.key}|${g.bbox.join(",")}|${g.stories.map((s) => `${s.id}:${s.updated_at}`).join("+")}`)
    .join(";");

  useEffect(() => {
    markersRef.current.forEach(({ marker }) => marker.remove());
    markersRef.current = new Map();
    if (!map || groups.length === 0) return;

    groups.forEach((group) => {
      const el = document.createElement("div");
      el.className = "listing-story-marker";
      el.title = group.stories.length === 1 ? group.stories[0].title : `${group.stories.length} story maps · ${group.label}`;
      const cards: string[] = [];
      if (group.stories.length > 2) cards.push('<div class="listing-story-card back2"></div>');
      if (group.stories.length > 1) cards.push('<div class="listing-story-card back1"></div>');
      const thumb = storyThumbnailUrl(group.stories[0], THUMB_W, THUMB_H);
      const style = thumb ? ` style="background-image:url(&quot;${thumb.replace(/"/g, "%22")}&quot;)"` : "";
      cards.push(`<div class="listing-story-card front"${style}></div>`);
      if (group.stories.length > 1) cards.push(`<span class="listing-story-count">${group.stories.length}</span>`);
      el.innerHTML = cards.join("");

      // Markers sit inside the map's canvas container, so a click would also
      // reach the map (and open a folder box beneath). Stop it here.
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        if (group.stories.length === 1) onOpenStoryRef.current?.(group.stories[0].id);
        else onOpenStackRef.current?.(group, el);
      });
      el.addEventListener("mousedown", (e) => e.stopPropagation());
      el.addEventListener("mouseenter", () => onHoverRef.current?.(group.key));
      el.addEventListener("mouseleave", () => onHoverRef.current?.(null));

      const [w, s, e, n] = group.bbox;
      const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat([(w + e) / 2, (s + n) / 2])
        .addTo(map);
      markersRef.current.set(group.key, { marker, el });
    });

    return () => {
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current = new Map();
      onHoverRef.current?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);

  useEffect(() => {
    markersRef.current.forEach(({ el }, key) => {
      el.classList.toggle("hovered", hoveredKeys.has(key));
    });
  }, [hoveredKeys, signature]);
}
